import institutionIcons from '@/assets/images/institutions';
import { SearchBar, SearchBarHandle } from '@/components/common/SearchBar';
import Add from '@mui/icons-material/Add';
import HouseRounded from '@mui/icons-material/HouseRounded';
import Search from '@mui/icons-material/Search';
import {
  Stack,
  Typography,
  Grid,
  List,
  Divider,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  DialogContent,
  Link,
} from '@mui/material';
import { t } from 'i18next';
import { IInstitution, RECOMMENDATIONS } from '../accountTypeData';
import InstitutionListItem from './InstitutionListItem';
import RecommendationCard from './RecommendationCard';
import { useRef, useCallback } from 'react';
import {
  FinancialOptions,
  StepsForManual,
  StepsForRecommended,
} from '../useAddAccountNavigation';
import SearchLoading from './SearchLoading';
import useFilteredResults from './useFilteredResults';
import CategoryUtility, { ClassificationItem } from '../categories.utility';
import { Classification } from '@/store/classificationsApi';
import { ClassificationIds } from '@/utils/enums';

interface StepOneConnectToFinancialInstitutionProps {
  updateSelected: (institution: IInstitution) => void;
  updateModalExperience: (experience: FinancialOptions) => void;
  updateModalStep: (step: StepsForManual | StepsForRecommended) => void;
  handleClassificationClick: (
    classification: ClassificationItem | null,
  ) => void;
  classifications: Classification[];
  isLoading: boolean;
}

const ConnectToFinancialInstitution = ({
  updateSelected,
  updateModalExperience,
  updateModalStep,
  handleClassificationClick,
  classifications,
  isLoading = false,
}: StepOneConnectToFinancialInstitutionProps) => {
  const searchBarRef = useRef<SearchBarHandle>(null);

  const {
    handleSearchChange,
    institutionSearch,
    isSearchLoading,
    filteredResults,
    topResults,
    categoryFilter,
    handleInstitutionTypeChange,
    categoryList,
    filteredByCategory,
  } = useFilteredResults();

  // Handler for hard-coded recommendation cards to populate the search bar
  const handleRecommendationCardClick = useCallback(
    (institution: IInstitution) => {
      searchBarRef.current?.setSearch(institution.label);
      updateSelected(institution);
    },
    [updateSelected],
  );

  /**
   * When a user clicks on "Add manually",
   * drop them into that workflow.
   */
  const handleManualCardClick = () => {
    updateModalExperience(FinancialOptions.MANUAL);
    updateModalStep(StepsForManual.MANUAL_ACCOUNT_TYPE);
  };

  /**
   * When a user clicks on "Add Real Estate",
   * drop them into that workflow in step 2.
   */
  const handleAddRealEstateClick = () => {
    const realEstateClassification =
      CategoryUtility.getClassificationByClassificationId({
        classifications,
        classificationId: ClassificationIds.REAL_ESTATE,
      });
    handleClassificationClick(realEstateClassification!);
    updateModalExperience(FinancialOptions.MANUAL);
    updateModalStep(StepsForManual.MANUAL_REAL_ESTATE_DETAILS);
  };

  /**
   * When a user clicks on a Recommended option, advance
   * them into that workflow.
   */
  const handleRecommendedResultClick = ({
    modalStep,
    institution,
  }: {
    modalStep: StepsForRecommended;
    institution: IInstitution;
  }) => {
    updateModalExperience(FinancialOptions.RECOMMENDED);
    updateModalStep(modalStep);
    updateSelected(institution);
  };

  return (
    <>
      <DialogContent>
        <Stack gap={5}>
          <Typography
            variant='h5'
            mt={0}
          >
            {t(
              'addAccountModal.connectToFinancialInstitution.connectToInstitution',
            )}
          </Typography>

          <Stack
            alignItems='flex-start'
            gap={0.5}
            sx={{ width: '100%' }}
          >
            <SearchBar
              ref={searchBarRef}
              id='institution-search'
              label={t(
                'addAccountModal.connectToFinancialInstitution.searchByNameOrUrl',
              )}
              onSearch={(value) => {
                handleSearchChange(value);
              }}
            />
          </Stack>

          {institutionSearch.length === 0 ? (
            <Stack gap={3}>
              <Typography
                variant='body1'
                align='center'
              >
                {t(
                  'addAccountModal.connectToFinancialInstitution.recommendedOptions',
                )}
              </Typography>

              <Grid
                container
                columnSpacing={3}
                rowSpacing={3}
              >
                <Grid
                  size={{ xs: 12, sm: 4, md: 4, lg: 4 }}
                  key='manual'
                >
                  <RecommendationCard
                    institution={{
                      label: t('addAccountModal.addManually'),
                      url: '',
                      value: 'manual',
                    }}
                    icon={
                      <Add
                        color='action'
                        sx={{ width: 32, height: 32 }}
                      />
                    }
                    onClick={handleManualCardClick}
                    isLoading={isLoading}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4 }}>
                  <RecommendationCard
                    institution={{
                      label: t('addAccountModal.addRealEstate'),
                      url: '',
                      value: 'manual',
                    }}
                    icon={<HouseRounded color='action' />}
                    onClick={handleAddRealEstateClick}
                    isLoading={isLoading}
                  />
                </Grid>

                {RECOMMENDATIONS.map((item) => {
                  const icon =
                    institutionIcons[
                      item.value as keyof typeof institutionIcons
                    ];

                  return (
                    <Grid
                      size={{ xs: 12, sm: 4, md: 4, lg: 4 }}
                      key={item.value}
                    >
                      <RecommendationCard
                        institution={item}
                        icon={<img src={icon} />}
                        onClick={handleRecommendationCardClick}
                        isLoading={isLoading}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Stack>
          ) : isSearchLoading ? (
            <SearchLoading />
          ) : filteredResults.length === 0 ? (
            <Stack
              py={6}
              justifyContent='center'
              alignItems='center'
            >
              <Search
                sx={{ width: 96, height: 96 }}
                color='action'
              />

              <Stack gap={2}>
                <Typography
                  variant='h6'
                  align='center'
                >
                  {t(
                    'addAccountModal.connectToFinancialInstitution.noResultsFound',
                    {
                      searchTerm: institutionSearch,
                    },
                  )}
                </Typography>

                <Box>
                  <Typography
                    variant='body1'
                    align='center'
                  >
                    {t(
                      'addAccountModal.connectToFinancialInstitution.institutionNotAvailable',
                    )}{' '}
                    <Link
                      underline='hover'
                      onClick={() => {
                        updateModalExperience(FinancialOptions.MANUAL);
                        updateModalStep(StepsForManual.MANUAL_ACCOUNT_TYPE);
                      }}
                      sx={{ fontWeight: 600, cursor: 'pointer' }}
                    >
                      {t('addAccountModal.manuallyEnterAccount')}
                    </Link>
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          ) : (
            <Stack gap={3}>
              <Stack gap={1.5}>
                <Typography variant='subtitle1'>
                  {t(
                    'addAccountModal.connectToFinancialInstitution.topResults',
                  )}
                </Typography>

                <List>
                  {topResults.map((item, index) => (
                    <InstitutionListItem
                      key={index}
                      item={item}
                      onClick={() => {
                        handleRecommendedResultClick({
                          modalStep: StepsForRecommended.RECOMMENDED_SELECTION,
                          institution: item,
                        });
                      }}
                    />
                  ))}
                </List>
              </Stack>

              <Divider />

              <Stack gap={1.5}>
                <Grid container>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 8,
                    }}
                  >
                    <Typography
                      variant='subtitle1'
                      sx={{ marginBottom: 3 }}
                    >
                      {t(
                        'addAccountModal.connectToFinancialInstitution.allResults',
                        {
                          count: filteredResults.length,
                        },
                      )}
                    </Typography>
                  </Grid>

                  <Grid
                    size={{
                      xs: 12,
                      sm: 4,
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel id='institution-type'>
                        {t(
                          'addAccountModal.connectToFinancialInstitution.institutionType',
                        )}
                      </InputLabel>
                      <Select
                        labelId='institution-type'
                        id='institution-type-select'
                        value={categoryFilter}
                        label={t(
                          'addAccountModal.connectToFinancialInstitution.institutionType',
                        )}
                        onChange={handleInstitutionTypeChange}
                      >
                        {categoryList.map((category) => (
                          <MenuItem
                            value={category}
                            key={category}
                          >
                            {t(
                              `addAccountModal.connectToFinancialInstitution.dropdownOptions.${category}`,
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <List>
                  {filteredByCategory.map((item) => (
                    <InstitutionListItem
                      key={item.institution.InstitutionId}
                      item={item}
                      onClick={() => {
                        handleRecommendedResultClick({
                          modalStep: StepsForRecommended.RECOMMENDED_SELECTION,
                          institution: item,
                        });
                      }}
                    />
                  ))}
                </List>
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      {/* No footer */}
    </>
  );
};

export default ConnectToFinancialInstitution;
